import { Directive, ElementRef, Input, NgZone, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { fromEvent, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Directive({
  selector: '[touchHover]',
  standalone: true,
})
export class TouchHoverDirective implements OnInit, OnDestroy {
  @Input() touchHoverDuration = 150;
  @Input() touchHoverClass = 'touch-hover';

  private removeTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private ngZone: NgZone,
  ) {}

  ngOnInit(): void {
    const el = this.el.nativeElement;
    this.ngZone.runOutsideAngular(() => {
      fromEvent<TouchEvent>(el, 'touchstart', { passive: true })
        .pipe(takeUntil(this.destroy$))
        .subscribe(event => this.onTouchStart(event));
      fromEvent<TouchEvent>(el, 'touchend', { passive: true })
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.onTouchEnd());
      fromEvent<TouchEvent>(el, 'touchcancel', { passive: true })
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.onTouchCancel());
    });
  }

  ngOnDestroy(): void {
    if (this.removeTimeoutId) {
      clearTimeout(this.removeTimeoutId);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  onTouchStart(event: TouchEvent): void {
    if (event.touches.length > 1) {
      return;
    }

    if (this.removeTimeoutId) {
      clearTimeout(this.removeTimeoutId);
      this.removeTimeoutId = null;
    }

    this.renderer.addClass(this.el.nativeElement, this.touchHoverClass);
  }

  onTouchEnd(): void {
    this.scheduleRemoval();
  }

  onTouchCancel(): void {
    this.scheduleRemoval();
  }

  private scheduleRemoval(): void {
    if (this.removeTimeoutId) {
      clearTimeout(this.removeTimeoutId);
    }

    this.removeTimeoutId = setTimeout(() => {
      this.renderer.removeClass(this.el.nativeElement, this.touchHoverClass);
      this.removeTimeoutId = null;
    }, this.touchHoverDuration);
  }
}
